import React from 'react';
import type { DimensionValue, TouchableOpacityProps } from 'react-native';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import { pointerEventsStyles, COLORS, ELEVATIONS, theme } from '@garnish/constants';

import { LoadingDots } from '../../LoadingDots';
import { BodyText } from '../../Text';

export const ButtonSearch = (props: ButtonSearchProps) => {
  const {
    children = BUTTON_SEARCH_DEFAULT_TEXT,
    accessibilityLabel = BUTTON_SEARCH_DEFAULT_A11Y_LABEL,
    accessibilityHint = BUTTON_SEARCH_DEFAULT_A11Y_HINT,
    disabled,
    isLoading,
    onPress,
    style,
    width,
    ...restProps
  } = props;

  const widthStyles = useStyle(() => (width === undefined ? undefined : { width }), [width]);
  const isDisabled = Boolean(disabled) || Boolean(isLoading);
  const buttonStyles = [styles.button, widthStyles, style];
  const textStyles = isDisabled ? styles.textDisabled : styles.text;

  return (
    <TouchableOpacity
      {...restProps}
      activeOpacity={ACTIVE_OPACITY}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      accessibilityState={{ disabled: isDisabled, busy: Boolean(isLoading) }}
      aria-busy={isLoading}
      style={buttonStyles}
      disabled={isDisabled}
      onPress={onPress}
    >
      {isLoading ? (
        <LoadingDots color={COLORS.TEXT_COLOR} />
      ) : (
        <BodyText sizeMatch={['14']} style={[textStyles, pointerEventsStyles.none]}>
          {children}
        </BodyText>
      )}
    </TouchableOpacity>
  );
};

//
// ─── CONSTANTS ──────────────────────────────────────────────────────────────────
//

export const BUTTON_SEARCH_DEFAULT_TEXT = 'Search area';
export const BUTTON_SEARCH_DEFAULT_A11Y_LABEL = BUTTON_SEARCH_DEFAULT_TEXT;
export const BUTTON_SEARCH_DEFAULT_A11Y_HINT = 'Searches for location in selected map area';

const ACTIVE_OPACITY = 0.8;

//
// ─── STYLES ─────────────────────────────────────────────────────────────────────
//

const styles = StyleSheet.create({
  button: {
    height: 40,
    ...ELEVATIONS['4'],
    minWidth: 122,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing['6'],
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: COLORS.CREAM,
  },
  text: {
    color: COLORS.TEXT_COLOR,
  },
  textDisabled: {
    color: COLORS.OPACITY.DARK_KALE.LIGHT,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ButtonSearchProps = {
  isLoading?: boolean;
  width?: DimensionValue;
} & TouchableOpacityProps;
